/**
 * User menu dropdown component
 */

import React, { useState, useRef, useEffect } from 'react';
import { useAuthContext } from '../Auth/AuthProvider';
import { User, Settings, LogOut, Shield, Users, UserCircle } from 'lucide-react';
import { Button } from '../UI/Button';

export interface UserMenuProps {
  onNavigate?: (path: string) => void;
}

export function UserMenu({ onNavigate }: UserMenuProps) {
  const { user, username, logout } = useAuthContext();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
      >
        <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-lg ring-2 ring-blue-100 dark:ring-blue-900">
          <User className="h-5 w-5 text-white" />
        </div>
        <div className="hidden md:block text-left">
          <div className="text-sm font-semibold text-slate-700 dark:text-gray-300">
            {user?.full_name || username || 'User'}
          </div>
          <div className="text-xs text-slate-500 dark:text-gray-400">
            {user?.email || ''}
          </div>
        </div>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-gray-200 dark:border-slate-700 py-2 z-50">
          {/* User Info */}
          <div className="px-4 py-3 border-b border-gray-200 dark:border-slate-700">
            <div className="font-semibold text-gray-900 dark:text-gray-100">
              {user?.full_name || username || 'User'}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 truncate" title={user?.email || ''}>
              {user?.email || ''}
            </div>
            {/* {user?.tenant_id && (
              <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                Tenant: {user.tenant_id.substring(0, 8)}...
              </div>
            )} */}
          </div>

          {/* Menu Items */}
          <div className="py-1">
            <button
              onClick={() => {
                setIsOpen(false);
                if (onNavigate) {
                  onNavigate('/profile');
                } else {
                  window.location.hash = '#/profile';
                }
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700"
            >
              <UserCircle className="h-4 w-4" />
              Profile
            </button>
            <button
              onClick={() => {
                setIsOpen(false);
                if (onNavigate) {
                  onNavigate('/teams');
                } else {
                  window.location.hash = '#/teams';
                }
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700"
            >
              <Users className="h-4 w-4" />
              Team Management
            </button>
            <button
              onClick={() => {
                setIsOpen(false);
                if (onNavigate) {
                  onNavigate('/settings');
                } else {
                  window.location.hash = '#/settings';
                }
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700"
            >
              <Settings className="h-4 w-4" />
              Settings
            </button>
          </div>

          {/* Logout */}
          <div className="border-t border-gray-200 dark:border-slate-700 pt-1">
            <button
              onClick={() => {
                setIsOpen(false);
                logout();
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <LogOut className="h-4 w-4" />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


